import React, { useState, useEffect, useCallback, useRef } from 'react';
import { get } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { initiateBulkDownload, getBulkDownloadStatus, getBulkDownloadUrl } from '../services/bulkDownloadApi';

function PhotosPage() {
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [previousTokens, setPreviousTokens] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState(new Set());

  // Bulk download state
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedImageSize, setSelectedImageSize] = useState('medium');
  const [downloadJob, setDownloadJob] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const pollIntervalRef = useRef(null);

  const isLocalDev = process.env.REACT_APP_LOCAL_DEV || false;

  // Helper function to get auth headers
  const getAuthHeaders = useCallback(async () => {
    // Skip authentication in local development mode
    if (isLocalDev) {
      return {};
    }

    const session = await fetchAuthSession();
    return {
      Authorization: `Bearer ${session.tokens?.idToken?.toString()}`
    };
  }, [isLocalDev]);

  const fetchPhotos = useCallback(async (token = null) => {
    setIsLoading(true);
    try {
      const apiName = 'AdminAPI';
      let path = '/api/v1/admin/photos?limit=20';

      if (token) {
        path += `&nextToken=${encodeURIComponent(token)}`;
      }

      // Get auth headers (may be empty for local development)
      const headers = await getAuthHeaders();

      try {
        const restOperation = get({
          apiName: apiName,
          path: path,
          options: {
            headers: headers
          }
        });

        const response = await restOperation.response;

        // Parse the ReadableStream body as JSON
        const photosData = await response.body.json();

        if (photosData && photosData.photos) {
          setPhotos(photosData.photos);
          setNextToken(photosData.nextToken || null);
        } else {
          console.error('Unexpected data format:', photosData);
          throw new Error('Unexpected data format in API response');
        }
      } catch (error) {
        console.error('Error fetching photos:', error);
        throw error;
      }
    } catch (error) {
      console.error('error fetching photos', error);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const getPresignedUrls = async (photoId) => {
    try {
      const apiName = 'AdminAPI';
      const path = `/api/v1/admin/photos/${photoId}/presigned-url`;

      // Get auth headers (may be empty for local development)
      const headers = await getAuthHeaders();

      const restOperation = get({
        apiName: apiName,
        path: path,
        options: {
          headers: headers
        }
      });
      const response = await restOperation.response;

      // Parse the ReadableStream body as JSON
      const presignedUrlData = await response.body.json();

      if (presignedUrlData && presignedUrlData.mediumUrl && presignedUrlData.fullUrl) {
        return {
          mediumUrl: presignedUrlData.mediumUrl,
          fullUrl: presignedUrlData.fullUrl
        };
      } else {
        console.error('Unexpected presigned URL format:', presignedUrlData);
        throw new Error('Unexpected data format in presigned URL response');
      }
    } catch (error) {
      console.error('Error fetching presigned urls:', error);
      return null;
    }
  };

  const openModal = async (photo) => {
    if (selectionMode) {
      togglePhotoSelection(photo.photoId);
      return;
    }
    setSelectedPhoto(photo);
    const urls = await getPresignedUrls(photo.photoId);
    if (urls) {
      setSelectedPhotoUrl(urls.mediumUrl);
      setModalOpen(true);
    }
  };

  const downloadPhoto = async (photo, event) => {
    event.stopPropagation(); // Prevent opening modal when clicking download
    try {
      const urls = await getPresignedUrls(photo.photoId);
      if (urls && urls.fullUrl) {
        // Open the presigned URL which now includes Content-Disposition: attachment
        // This will trigger a download instead of opening in a new tab
        window.open(urls.fullUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading photo:', error);
    }
  };

  const closeModal = () => {
    setSelectedPhoto(null);
    setSelectedPhotoUrl('');
    setModalOpen(false);
  };

  const handleNextPage = () => {
    if (nextToken && !isLoading) {
      setPreviousTokens([...previousTokens, nextToken]);
      fetchPhotos(nextToken);
    }
  };

  const handlePreviousPage = () => {
    if (previousTokens.length > 0 && !isLoading) {
      const newPreviousTokens = [...previousTokens];
      newPreviousTokens.pop();
      setPreviousTokens(newPreviousTokens);

      const previousToken = newPreviousTokens[newPreviousTokens.length - 1] || null;
      fetchPhotos(previousToken);
    }
  };

  // Selection mode functions
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      setSelectedPhotoIds(new Set());
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotoIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(photoId)) {
        newSet.delete(photoId);
      } else {
        newSet.add(photoId);
      }
      return newSet;
    });
  };

  const selectAllOnPage = () => {
    setSelectedPhotoIds(new Set(photos.map(p => p.photoId)));
  };

  const deselectAll = () => {
    setSelectedPhotoIds(new Set());
  };

  const handleCheckboxClick = (e, photoId) => {
    e.stopPropagation();
    togglePhotoSelection(photoId);
  };

  // Bulk download functions
  const openDownloadModal = () => {
    setDownloadModalOpen(true);
    setDownloadJob(null);
  };

  const closeDownloadModal = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setDownloadModalOpen(false);
    setDownloadJob(null);
    setIsDownloading(false);
  };

  const startBulkDownload = async (downloadAll = false) => {
    setIsDownloading(true);
    try {
      const photoIds = downloadAll ? null : Array.from(selectedPhotoIds);
      const response = await initiateBulkDownload(selectedImageSize, photoIds);
      setDownloadJob(response);

      // Start polling for status
      pollIntervalRef.current = setInterval(async () => {
        try {
          const status = await getBulkDownloadStatus(response.jobId);
          setDownloadJob(status);

          if (status.status === 'completed' || status.status === 'failed') {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setIsDownloading(false);
          }
        } catch (error) {
          console.error('Error polling download status:', error);
        }
      }, 3000);
    } catch (error) {
      console.error('Error starting bulk download:', error);
      setIsDownloading(false);
    }
  };

  const handleDownloadZip = async () => {
    if (!downloadJob || downloadJob.status !== 'completed') return;

    try {
      const urlResponse = await getBulkDownloadUrl(downloadJob.jobId);
      if (urlResponse && urlResponse.downloadUrl) {
        window.open(urlResponse.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Error getting download URL:', error);
    }
  };

  const getProgressPercent = () => {
    if (!downloadJob || !downloadJob.totalPhotos) return 0;
    return Math.round(((downloadJob.processedPhotos || 0) / downloadJob.totalPhotos) * 100);
  };

  return (
    <div>
      {/* Header controls */}
      <div className="photos-header">
        <div className="header-left">
          <button
            className={`selection-toggle-btn ${selectionMode ? 'active' : ''}`}
            onClick={toggleSelectionMode}
          >
            {selectionMode ? 'Exit Selection' : 'Select Photos'}
          </button>
          {selectionMode && (
            <>
              <button className="selection-btn" onClick={selectAllOnPage}>
                Select All on Page
              </button>
              <button className="selection-btn" onClick={deselectAll}>
                Deselect All
              </button>
              <span className="selection-count">
                {selectedPhotoIds.size} selected
              </span>
            </>
          )}
        </div>
        <div className="header-right">
          {selectionMode && selectedPhotoIds.size > 0 && (
            <button className="download-selected-btn" onClick={openDownloadModal}>
              Download Selected ({selectedPhotoIds.size})
            </button>
          )}
          <button className="download-all-btn" onClick={openDownloadModal}>
            Download All Photos
          </button>
        </div>
      </div>

      <div className="gallery">
        {photos.map((photo) => (
          <div
            key={photo.photoId}
            className={`thumbnail ${selectionMode ? 'selection-mode' : ''} ${selectedPhotoIds.has(photo.photoId) ? 'selected' : ''}`}
            onClick={() => openModal(photo)}
          >
            {selectionMode && (
              <input
                type="checkbox"
                className="photo-checkbox"
                checked={selectedPhotoIds.has(photo.photoId)}
                onChange={(e) => handleCheckboxClick(e, photo.photoId)}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <div className="thumbnail-image-container">
              <img src={photo.thumbnailUrl} alt={`thumbnail-${photo.photoId}`} />
            </div>
            <div className="thumbnail-info">
              <p className="uploader-name">Uploader: {photo.uploaderName}</p>
              {!selectionMode && (
                <button
                  className="download-btn"
                  onClick={(e) => downloadPhoto(photo, e)}
                  title="Download photo"
                >
                  Download
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {(photos.length > 0 || previousTokens.length > 0) && (
        <div className="pagination">
          <button
            onClick={handlePreviousPage}
            disabled={previousTokens.length === 0 || isLoading}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {previousTokens.length + 1}
          </span>
          <button
            onClick={handleNextPage}
            disabled={!nextToken || isLoading}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Photo preview modal */}
      {modalOpen && selectedPhoto && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={closeModal}>&times;</span>
            {selectedPhotoUrl ? (
              <img src={selectedPhotoUrl} alt={`full-${selectedPhoto.photoId}`} />
            ) : (
              <p>Loading...</p>
            )}
            <p>Uploader: {selectedPhoto.uploaderName}</p>
          </div>
        </div>
      )}

      {/* Bulk download modal */}
      {downloadModalOpen && (
        <div className="modal">
          <div className="modal-content download-modal">
            <span className="close" onClick={closeDownloadModal}>&times;</span>
            <h2>Download Photos</h2>

            {!downloadJob && (
              <>
                <div className="download-options">
                  <label>Image Size:</label>
                  <select
                    value={selectedImageSize}
                    onChange={(e) => setSelectedImageSize(e.target.value)}
                    disabled={isDownloading}
                  >
                    <option value="full">Full Size (Original)</option>
                    <option value="medium">Medium (800px)</option>
                    <option value="small">Small (200px)</option>
                  </select>
                </div>

                <div className="download-actions">
                  {selectionMode && selectedPhotoIds.size > 0 ? (
                    <button
                      className="start-download-btn"
                      onClick={() => startBulkDownload(false)}
                      disabled={isDownloading}
                    >
                      {isDownloading ? 'Starting...' : `Download Selected (${selectedPhotoIds.size} photos)`}
                    </button>
                  ) : (
                    <button
                      className="start-download-btn"
                      onClick={() => startBulkDownload(true)}
                      disabled={isDownloading}
                    >
                      {isDownloading ? 'Starting...' : 'Download All Photos'}
                    </button>
                  )}
                </div>
              </>
            )}

            {downloadJob && (
              <div className="download-progress">
                <div className="progress-status">
                  Status: <strong>{downloadJob.status}</strong>
                </div>

                {(downloadJob.status === 'processing' || downloadJob.status === 'pending') && (
                  <>
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar"
                        style={{ width: `${getProgressPercent()}%` }}
                      />
                    </div>
                    <div className="progress-text">
                      {downloadJob.processedPhotos || 0} / {downloadJob.totalPhotos || 0} photos processed ({getProgressPercent()}%)
                    </div>
                    <p className="progress-note">Please wait while your download is being prepared...</p>
                  </>
                )}

                {downloadJob.status === 'completed' && (
                  <div className="download-ready">
                    <p>Your download is ready!</p>
                    <button className="download-zip-btn" onClick={handleDownloadZip}>
                      Download ZIP File
                    </button>
                  </div>
                )}

                {downloadJob.status === 'failed' && (
                  <div className="download-failed">
                    <p>Download failed: {downloadJob.errorMessage || 'Unknown error'}</p>
                    <button
                      className="retry-btn"
                      onClick={() => setDownloadJob(null)}
                    >
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PhotosPage;
